import * as Y from 'yjs';
import type { ObjectData } from './schema';

/** The page of every board written before P14 (plan P14 Ruling 4): fixed, so peers that materialize it converge. */
export const PAGE_MAIN = 'main';
/** Commands refuse a 51st page; a board past it (concurrent adds) is still read whole (Ruling 5). */
export const PAGE_LIMIT = 50;
export const PAGE_NAME_MAX = 100;

/** One page of the board; an empty `name` is displayed "Page N" (no localized text in the document). */
export type PageEntry = { id: string; name: string; order: string };

export const IMPLICIT_PAGE: Readonly<PageEntry> = Object.freeze({ id: PAGE_MAIN, name: '', order: 'a0' });

const validId = (id: string) => id.length >= 1 && id.length <= 64;

/**
 * The board's pages in display order `(order, id)`, never empty: an empty `pages` map (any board written before P14)
 * reads as the implicit `main` page. Never writes; an invalid entry (forged peer) is left out.
 */
export function readPages(doc: Y.Doc): PageEntry[] {
  const out: PageEntry[] = [];
  doc.getMap<unknown>('pages').forEach((v, id) => {
    if (!(v instanceof Y.Map) || !validId(id)) return;
    const name = v.get('name');
    const order = v.get('order');
    if (typeof name !== 'string' || typeof order !== 'string' || order === '') return;
    out.push({ id, name: name.slice(0, PAGE_NAME_MAX), order });
  });
  if (out.length === 0) return [{ ...IMPLICIT_PAGE }];
  return out.sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/**
 * Each object's effective page (Ruling 5): the `pageId` of its root ancestor when that page exists, else the first
 * page. A missing parent makes a root; a parent cycle is rooted at its smallest id, as `normalizeObjects` cuts it.
 */
export function pageAssignment(objects: ReadonlyMap<string, ObjectData>, pages: readonly PageEntry[]): Map<string, string> {
  const known = new Set(pages.map((p) => p.id));
  const first = pages[0]?.id ?? PAGE_MAIN;
  const rootOf = new Map<string, string>();
  for (const start of objects.keys()) {
    if (rootOf.has(start)) continue;
    const path: string[] = [];
    let cur: string | null = start;
    let root: string | null = null;
    while (cur !== null) {
      const done = rootOf.get(cur);
      if (done !== undefined) {
        root = done;
        break;
      }
      const loop = path.indexOf(cur);
      if (loop >= 0) {
        root = path.slice(loop).reduce((a, b) => (b < a ? b : a));
        break;
      }
      path.push(cur);
      const parent: string | null = objects.get(cur)?.parentId ?? null;
      if (parent === null || !objects.has(parent)) {
        root = cur;
        break;
      }
      cur = parent;
    }
    for (const id of path) rootOf.set(id, root ?? start);
  }
  const out = new Map<string, string>();
  for (const [id] of objects) {
    const page = objects.get(rootOf.get(id) ?? id)?.pageId;
    out.set(id, page !== undefined && known.has(page) ? page : first);
  }
  return out;
}
